// Trip money: profit, margin, fiscal-year labels and the CSV export. Safe to
// bundle in the browser, so the web app shares it with the API.

import { fiscalYearOf } from '../../../src/fiscal-year.ts';
import type { TripMoneyFacts, TripProfit, TripSource, TripSummary } from './api-types.ts';

export const round2 = (n: number) => Math.round(n * 100) / 100;

/** profit / income as a fraction to four places; null on zero income. */
export const marginOf = (profit: number, income: number): number | null => (income === 0 ? null : Math.round((profit / income) * 10_000) / 10_000);

/** Profit and margin from income and cost: null without income; no margin on zero income. */
export function profitOf(income: number | null, cost: number): { profit: number | null; margin: number | null } {
  if (income === null) return { profit: null, margin: null };
  const profit = round2(income - cost);
  return { profit, margin: marginOf(profit, income) };
}

/**
 * Profit two ways. Projected: the invoice, else the trip price, less the contracted vendor cost or what's been
 * paid if that's more. Today: money received less Salesforce payments.
 */
export function tripProfit(f: TripMoneyFacts): TripProfit {
  const projectedIncome = f.invoiced ?? f.price;
  const projectedCost = round2(Math.max(f.contracted ?? 0, f.paid));
  const projected = profitOf(projectedIncome, projectedCost);
  const today = profitOf(f.received, f.paid);
  return {
    projectedIncome, projectedCost, projectedProfit: projected.profit, projectedMargin: projected.margin,
    profitToday: today.profit, marginToday: today.margin,
  };
}

/** Xero cost less Salesforce paid. Null when Xero wasn't read. */
export const costDifference = (t: Pick<TripSummary, 'paid' | 'xero'>): number | null => (t.xero ? round2(t.xero.cost - t.paid) : null);
/** More than a dollar apart. */
export const differs = (t: Pick<TripSummary, 'paid' | 'xero'>) => Math.abs(costDifference(t) ?? 0) > 1;

/** Fiscal year by trip date, named for the year it ends: with a Dec 1 start, 'FY26' runs Dec 1, 2025 to Nov 30, 2026. */
export function fiscalYearLabel(date: string, fiscalYearStart: string): string {
  return `FY${String(fiscalYearOf(date, fiscalYearStart) % 100).padStart(2, '0')}`;
}
export const FY_PARAM = /^FY\d{2}$/;

const percent = (m: number | null) => (m === null ? null : Math.round(m * 10_000) / 100);

function csvCell(v: string | number | null): string {
  if (v === null) return '';
  if (typeof v === 'number') return v.toFixed(2);
  // A leading = + - @ would run as a formula in a spreadsheet.
  const text = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

type CsvColumn = [string, (t: TripSummary) => string | number | null];

const SALESFORCE_COLUMNS: CsvColumn[] = [
  ['Trip', (t) => t.name], ['Trip date', (t) => t.date], ['Trip price', (t) => t.price], ['Invoiced', (t) => t.invoiced],
  ['Contracted cost', (t) => t.contracted], ['Projected cost', (t) => t.projectedCost], ['Projected profit', (t) => t.projectedProfit],
  ['Projected margin %', (t) => percent(t.projectedMargin)], ['Received', (t) => t.received], ['Paid', (t) => t.paid],
  ['Profit today', (t) => t.profitToday], ['Margin today %', (t) => percent(t.marginToday)], ['In Xero', (t) => t.inXero], ['Reconciled in Xero', (t) => t.reconciledAmount],
];
/** Rows already carry Xero's money (withXero). */
const XERO_COLUMNS: CsvColumn[] = [
  ['Trip', (t) => t.name], ['Trip date', (t) => t.date], ['Invoiced in Xero', (t) => t.invoiced], ['Cost in Xero', (t) => t.paid],
  ['Profit', (t) => t.projectedProfit], ['Margin %', (t) => percent(t.projectedMargin)], ['Received in Xero', (t) => t.received],
  ['Profit today', (t) => t.profitToday], ['Margin today %', (t) => percent(t.marginToday)], ['Reconciled cost in Xero', (t) => t.reconciledAmount],
];
const COMPARE_COLUMNS: CsvColumn[] = [
  ...SALESFORCE_COLUMNS,
  ['Cost in Xero', (t) => t.xero?.cost ?? null], ['Reconciled cost in Xero', (t) => t.xero?.reconciledCost ?? null],
  ['Invoiced in Xero', (t) => t.xero?.invoiced ?? null], ['Received in Xero', (t) => t.xero?.received ?? null],
  ['Xero cost less Salesforce paid', (t) => costDifference(t)],
];

/** The trip list as CSV, one row per trip, in the order given. Money in dollars, margins in percent, empty for no value. */
export function tripsCsv(trips: TripSummary[], source: TripSource = 'salesforce'): string {
  const columns = source === 'xero' ? XERO_COLUMNS : source === 'compare' ? COMPARE_COLUMNS : SALESFORCE_COLUMNS;
  const rows = trips.map((t) => columns.map(([, get]) => csvCell(get(t))).join(','));
  return [columns.map(([head]) => head).join(','), ...rows].join('\r\n') + '\r\n';
}
